import {useCallback} from 'react';

import {
    SearchCompanies$Request,
    searchCompaniesFailure,
    searchCompaniesRequest,
    searchCompaniesSuccess,
} from '/src/openapi/ccdb-bff/apis/CompaniesApi';
import {CcdbCompany} from '/src/openapi/ccdb-bff/models/CcdbCompany';
import {useAppDispatch, useAppSelector} from '/src/store/hooks';
import {ROOT} from '/src/middleware/api';

export const DEFAULT_SEARCH_PAGINATION = {
    page: 0,
    size: 20,
};

export const useCompanySearch = () => {
    const dispatch = useAppDispatch();
    const {searchResults, loading, error} = useAppSelector(state => state.ccdbCompanySearch);

    const doSearch = useCallback(async (params: SearchCompanies$Request) => {
        const meta = {
            searchValue: params.searchValue,
            searchField: params.searchField,
            page: params.page ?? DEFAULT_SEARCH_PAGINATION.page,
            size: params.size ?? DEFAULT_SEARCH_PAGINATION.size,
        };

        // Dispatch loading state
        dispatch(searchCompaniesRequest({data: undefined, meta, error: false}));

        try {
            const response = await fetch(`${ROOT}/api/companies/${encodeURIComponent(meta.searchValue)}`);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({code: response.status, message: `Company not found (HTTP ${response.status})`}));
                dispatch(searchCompaniesFailure({
                    data: errorData,
                    meta,
                    error: true,
                }));
                return;
            }

            const company: CcdbCompany = await response.json();

            // Wrap single company into CompanySearchResponse format
            dispatch(searchCompaniesSuccess({
                data: {
                    companies: [company],
                    totalElements: 1,
                    totalPages: 1,
                    page: 0,
                    size: meta.size,
                },
                meta,
                error: false,
            }));
        } catch (err) {
            dispatch(searchCompaniesFailure({
                data: {code: 0, message: err instanceof Error ? err.message : 'Network error occurred'},
                meta,
                error: true,
            }));
        }
    }, [dispatch]);

    return {
        searchResults,
        loading,
        error,
        doSearch,
    };
};
